// src/features/account/profileValidators.js

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ✅ Letters and spaces ONLY
export const NAME_RE = /^[A-Za-z ]+$/;

export const USERNAME_RE = /^[A-Za-z][A-Za-z0-9._]{2,19}$/;
export const PH_PHONE_RE = /^(09\d{9}|\+63\d{10})$/;
export const POSTAL_RE = /^\d{4,6}$/;
export const ADDRESS_RE = /^[A-Za-z0-9À-ÖØ-öø-ÿÑñ\s.,#\-/'()&]+$/;
export const PLACE_RE = /^[A-Za-z0-9À-ÖØ-öø-ÿÑñ\s.'-]+$/;
export const BIRTHDATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function clean(v) {
  return String(v ?? "").trim();
}

export function normalizePhone(input) {
  let v = clean(input);
  if (!v) return "";

  v = v.replace(/[\s-]/g, "");
  v = v.replace(/[^0-9+]/g, "");

  if (/^63\d{10}$/.test(v)) return `+${v}`;
  if (/^09\d{9}$/.test(v)) return v;
  if (/^\+63\d{10}$/.test(v)) return v;

  return v;
}

export function normalizeBirthdate(input) {
  const v = clean(input);
  if (!v) return "";

  // Extract YYYY-MM-DD from various formats
  // Handles: "2000-01-15", "2000-01-15T00:00:00Z", "2000-01-15T00:00:00", etc.
  const match = v.match(/(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : v;
}

export function validateField(key, raw) {
  const v = clean(raw);

  if (key === "firstName") {
    if (!v) return "First name is required.";
    if (v.length < 2) return "First name must be at least 2 characters.";
    if (!NAME_RE.test(v)) return "First name must contain letters and spaces only.";
    return null;
  }

  if (key === "gender") {
    if (!v) return null; // optional
    const up = v.toUpperCase();
    if (!["MALE", "FEMALE", "OTHER"].includes(up))
      return "Gender must be MALE, FEMALE, or OTHER.";
    return null;
  }

  if (key === "lastName") {
    if (!v) return "Last name is required.";
    if (v.length < 2) return "Last name must be at least 2 characters.";
    if (!NAME_RE.test(v)) return "Last name must contain letters and spaces only.";
    return null;
  }

  if (key === "username") {
    if (!v) return "Username is required.";
    if (!USERNAME_RE.test(v))
      return "3–20 chars, start with letter, use letters/numbers/._ only.";
    return null;
  }

  if (key === "email") {
    if (!v) return "Email is required.";
    if (!EMAIL_RE.test(v)) return "Invalid email format.";
    return null;
  }

  if (key === "phone") {
    if (!v) return null; // optional
    const normalized = normalizePhone(v);
    if (!PH_PHONE_RE.test(normalized))
      return "Use 09XXXXXXXXX or +63XXXXXXXXXX.";
    return null;
  }

  if (key === "birthdate") {
    if (!v) return null; // optional
    if (!BIRTHDATE_RE.test(v)) return "Birthdate must be in YYYY-MM-DD format.";
    // Check if date is valid
    const date = new Date(v);
    if (Number.isNaN(date.getTime())) return "Invalid date.";
    // Check if birthdate is not in the future
    if (date > new Date()) return "Birthdate cannot be in the future.";
    return null;
  }

  if (key === "postalCode") {
    if (!v) return null; // optional
    if (!POSTAL_RE.test(v)) return "Postal code must be 4–6 digits.";
    return null;
  }

  if (key === "city") {
    if (!v) return null;
    if (!PLACE_RE.test(v)) return "City contains invalid characters.";
    if (v.length < 2) return "City is too short.";
    return null;
  }

  if (key === "province") {
    if (!v) return null;
    if (!PLACE_RE.test(v)) return "Province contains invalid characters.";
    if (v.length < 2) return "Province is too short.";
    return null;
  }

  if (key === "addressLine1" || key === "addressLine2") {
    if (!v) return key === "addressLine1" ? "Address line 1 is required." : null;
    if (!ADDRESS_RE.test(v)) return "Address contains invalid characters.";
    if (v.length < 6) return "Address looks too short.";
    return null;
  }

  return null;
}
